import { resolvePatternText } from '@/shared/patterns/text';
import { useState } from 'react';
import { formatScalar } from '@/shared/patterns/values';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { Pencil, ReceiptText } from 'lucide-react';
import type { z } from 'zod';
import { defineBlock, type BlockViewProps } from '@/shared/page';
import { columnsFor, DataView } from '@/shared/patterns';
import {
  billingListParams,
  billingListSource,
  billingDetailParams,
  billingDetailSource,
} from './billing.sources';
import {
  billingOpen,
  billingOpenWallet,
  billingRegisterFromList,
  billingEdit,
  billingPage,
  billingSelect,
} from './billing.screen';
import { BILLING_STATUS } from './billing.enums';
import type { Billing } from './billing.wire';
import { formatCurrencyInCents } from '@/shared/http';
const col = columnsFor<Billing>();
export const billingColumns = [
  col.text('documentNumber', msg`Nº do documento`, { mobile: 'title' }),
  col.text('payer.name', msg`Devedor`, { mobile: 'subtitle' }),
  col.text('walletCode', msg`Código convênio`),
  col.text('ourNumber', msg`Nosso número`),
  col.money('amount', msg`Valor da cobrança`),
  col.date('createdAt', msg`Criado em`),
  col.date('dueDate', msg`Vencimento`),
  col.status('statusValue', msg`Status`, BILLING_STATUS.statuses, {
    fallbackField: 'statusDisplay',
  }),
];
const optionalColumns = [
  col.text('payer.email', msg`Email do devedor`),
  col.document('payer.registrationNumber', msg`Documento do devedor`),
  col.text('payer.personTypeValueDisplay', msg`Tipo de pessoa`),
  col.text('bankSlip.statusRegisterValueDisplay', msg`Situação de registro`),
  col.text('description', msg`Descrição`),
  col.text('guarantor.name', msg`Sacador avalista`),
  col.document('guarantor.registrationNumber', msg`Documento do avalista`),
];
type ListProps = BlockViewProps<
  z.output<typeof billingListParams>,
  { list: typeof billingListSource }
>;
function ListView({
  params,
  data,
  run,
  presentation,
}: ListProps & { presentation: 'web' | 'mobile' }) {
  const { t, i18n } = useLingui();
  const [extra, setExtra] = useState<string[]>([]);
  return (
    <div>
      <details className="mb-3">
        <summary>{t`Colunas adicionais`}</summary>
        <div className="grid gap-2 p-3">
          {optionalColumns.map((column) => (
            <label key={column.field} className="flex gap-2">
              <input
                type="checkbox"
                checked={extra.includes(column.field)}
                onChange={(event) =>
                  setExtra(
                    event.target.checked
                      ? [...extra, column.field]
                      : extra.filter((value) => value !== column.field),
                  )
                }
              />
              {resolvePatternText(i18n, column.label)}
            </label>
          ))}
        </div>
      </details>
      <DataView<Billing>
        label={t`Resultados de cobranças`}
        columns={[
          ...billingColumns,
          ...optionalColumns.filter((column) => extra.includes(column.field)),
        ]}
        page={data.list}
        rowKey="id"
        presentation={presentation}
        framed={false}
        onOpen={(row) => run('billing.open', { id: row.id })}
        rowActions={(row) => [
          ...(!row.registered && row.statusValue !== 'Registering'
            ? [
                {
                  id: 'billing.registerFromList',
                  label: t`Registrar boleto`,
                  icon: ReceiptText,
                  onSelect: () => run('billing.registerFromList', { id: row.id }),
                },
              ]
            : []),
          ...(!row.registered || row.statusValue === 'NotRegistered'
            ? [
                {
                  id: 'billing.edit',
                  label: t`Edição rápida`,
                  icon: Pencil,
                  onSelect: () => run('billing.edit', { id: row.id }),
                },
              ]
            : []),
        ]}
        selection={{
          selected: params.selected,
          onChange: (ids) => run('billing.select', { ids }),
          bar: false,
        }}
        pagination={{
          pageIndex: params.page,
          pageSize: params.size,
          onChange: ({ pageIndex, pageSize }) =>
            run('billing.page', { page: pageIndex, size: pageSize }),
        }}
      />
    </div>
  );
}
const ListWeb = (p: ListProps) => <ListView {...p} presentation="web" />;
const ListMobile = (p: ListProps) => <ListView {...p} presentation="mobile" />;
export const billingListBlock = defineBlock({
  id: 'billing.list',
  title: msg`Cobranças`,
  description: 'Billing table on web and cards on iPhone.',
  status: 'screen',
  params: billingListParams,
  sources: { list: billingListSource },
  query: ({ page, size, filters }) => ({ list: { page, size, filters, selected: [] } }),
  actions: [billingOpen, billingRegisterFromList, billingEdit, billingPage, billingSelect],
  empty: {
    title: msg`Nenhuma cobrança`,
    description: msg`Ajuste os filtros ou cadastre uma cobrança.`,
    icon: ReceiptText,
  },
  skeleton: { web: { kind: 'table', rows: 5, columns: 8 }, mobile: { kind: 'list', rows: 4 } },
  render: { web: ListWeb, mobile: ListMobile },
});

type DetailProps = BlockViewProps<
  z.output<typeof billingDetailParams>,
  { record: typeof billingDetailSource }
>;
export const pendingPrincipal = (row: Pick<Billing, 'amount' | 'paidAmount'>) =>
  Math.max(0, row.amount - (row.paidAmount ?? 0));
function DetailView({ data, mobile, run }: DetailProps & { mobile: boolean }) {
  const { t, i18n } = useLingui();
  const row = data.record;
  // eslint-disable-next-line lingui/no-unlocalized-strings -- U02: backend permission and status identifiers.
  const paid = ['Paid', 'PartialPaid'].includes(row.statusValue);
  const fields = [
    [t`Código convênio`, String(row.walletCode)],
    [t`Número de documento`, row.documentNumber],
    [t`Número da carteira`, row.walletNumber],
    [t`Status da cobrança`, row.statusDisplay],
    [t`Criado em`, formatScalar('date', row.createdAt, i18n.locale)],
    [t`Devedor`, [row.payer?.name, row.payer?.registrationNumber].filter(Boolean).join(' · ')],
    [t`Email`, row.payer?.email],
    [t`Endereço`, [row.payer?.addressName, row.payer?.zipCode].filter(Boolean).join(' · ')],
    [t`Forma de envio`, t`Email`],
    [t`Espécie título`, row.creditTypeDisplay],
    [t`Descrição`, row.description],
    [t`Desconto`, formatCurrencyInCents(Number(row.discount ?? 0))],
    ...(row.discountLimitDate
      ? [[t`Data limite desconto`, formatScalar('date', row.discountLimitDate, i18n.locale)]]
      : []),
    ...(row.lastPaymentDate
      ? [[t`Data último pagamento`, formatScalar('date', row.lastPaymentDate, i18n.locale)]]
      : []),
    ...(paid ? [[t`Canal de pagamento`, row.settlementChannelValueDisplay]] : []),
    [t`Valor cobrança`, formatCurrencyInCents(row.amount)],
    ...(paid && (row.paidAmount ?? 0) > 0
      ? [[t`Valor pago`, formatCurrencyInCents(row.paidAmount ?? 0)]]
      : []),
    ...(row.statusValue === 'PartialPaid' && pendingPrincipal(row) > 0
      ? [[t`Valor pendente`, formatCurrencyInCents(pendingPrincipal(row))]]
      : []),
    ...(row.billingSettingDisplay ? [[t`Régua de cobrança`, row.billingSettingDisplay]] : []),
    ...(row.communicationSettingDisplay
      ? [[t`Régua de comunicação`, row.communicationSettingDisplay]]
      : []),
    [t`Categorias`, row.tags?.join(', ')],
  ];
  return (
    <dl className={mobile ? 'grid gap-4' : 'grid grid-cols-2 gap-6 xl:grid-cols-3'}>
      {fields.map(([label, value]) => (
        <div key={label}>
          <dt className="text-sm text-muted-foreground">{label}</dt>
          <dd className="mt-1 break-words text-foreground">
            {label === t`Código convênio` ? (
              <button
                type="button"
                className="cursor-pointer text-left underline"
                onClick={() => run('billing.openWallet', { code: String(row.walletCode) })}
              >
                {value}
              </button>
            ) : (
              value || t`N/D`
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
const DetailWeb = (p: DetailProps) => <DetailView {...p} mobile={false} />;
const DetailMobile = (p: DetailProps) => <DetailView {...p} mobile />;
export const billingDetailBlock = defineBlock({
  id: 'billing.data',
  title: msg`Dados da cobrança`,
  description: 'Billing amounts and payment information.',
  status: 'screen',
  params: billingDetailParams,
  sources: { record: billingDetailSource },
  query: (p) => ({ record: p }),
  // O convênio sai por intenção ao host (U41): a tela de detalhe resolve o destino.
  actions: [billingOpenWallet],
  render: { web: DetailWeb, mobile: DetailMobile },
});
